import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {ObjectTypes, ApiErrorCodes, SortDirections} from '../../../api/consts';
import {useTranslation} from 'react-i18next';
import {push as navigateTo} from 'connected-react-router';
import {useGenericResultsDataApi} from '../../../api/hooks';
import {useSMCSnackbar, useColumnsWidthsPersistedState, useSort, useColumnsStatePersistedState} from '../../../utils/hooks';
import {SMCPaths} from '../../../conf/Routes';
import store from '../../../store';
import {SystemLogsGetListSchema} from '../../../api/DBSchema/SystemLogs';
import {dateAndTimeToString} from '../../../utils/utils';
import infoIcon from '../../../assets/images/info.png';
import warningIcon from '../../../assets/images/warning.png';
import errorIcon from '../../../assets/images/error.png';
import globeIcon from '../../../assets/images/globe.png';
import computerIcon from '../../../assets/images/general.png';
import LogsToExcelSnackbarContent from '../../PopupPages/LogsToExcelSnackbarContent';
import {useSnackbar} from 'notistack';
import {Link} from 'react-router-dom';
import {buildNumberSort, buildStringSortCaseInsensitive} from '../../../utils/sortUtils';
import SMCHighlighter from '../../../components/Simple/SMCHighlighter';

function getCellColor(cell) {
    const logSeverity = cell[SystemLogsGetListSchema.logSeverity];
    let cellColor;
    if (logSeverity === SystemLogsGetListSchema.logSeverityValues.message) {
        cellColor = 'green';
    }
    if (logSeverity === SystemLogsGetListSchema.logSeverityValues.warning) {
        cellColor = '#CC6600';
    }
    if (logSeverity === SystemLogsGetListSchema.logSeverityValues.error) {
        cellColor = 'red';
    }

    return cellColor;
}

const severityCell = React.memo(({value}) => {
    const { t } = useTranslation();
    switch (value) {
        case 'MESSAGE':
            return <img src={infoIcon} title={t('SMP.SETTINGS.LOGGING.LOG_LEVEL.NOTICE')} alt="message" />;
        case 'WARNING':
            return <img src={warningIcon} title={t('SMP.SETTINGS.LOGGING.LOG_LEVEL.WARNING')} alt="warning" />;
        case 'ERROR':
            return <img src={errorIcon} title={t('SMP.SETTINGS.LOGGING.LOG_LEVEL.ERROR')} alt="error" />;
        default:
            console.error("Activity logs table - severity " + value + " not found icon")
            return null;
    }
});

const dateCell = React.memo(({value, object, textToHighlight}) => {
    let cellColor = getCellColor(object);
    return (
        <SMCHighlighter highlight={textToHighlight} style={{ fontSize: '8pt', color: cellColor }} text={dateAndTimeToString(value, true)} />
    )
});

const coloredCell = React.memo(({value, object, textToHighlight}) => {
    let cellColor = getCellColor(object);
    return (
        <SMCHighlighter highlight={textToHighlight} style={{ fontSize: '8pt', color: cellColor }} text={value} />
    )
});

const userCell = React.memo(({value, object, textToHighlight}) => {
    let cellColor = getCellColor(object);
    return (
        <Link to={SMCPaths.genericUser+'?id='+value}><SMCHighlighter style={{ fontSize: '8pt', color: cellColor, textDecoration: 'underline' }} highlight={textToHighlight} text={value} /></Link>
    );
});

const infoCell = React.memo(({object, textToHighlight}) => {
    let cellColor = getCellColor(object);
    const informationFields = object['informationFields'];
    if (!informationFields || !informationFields.length) {
        return null;
    }
    const information = (
        <>
        {
            informationFields.map((info, index) => {
                try {
                    if (info.key === 'msg') {
                        return null;
                    }
                    else if (info.key === 'ip') {
                        return <React.Fragment key={index}><b>ip:</b><SMCHighlighter text={" " + info.value.ipString} /></React.Fragment>
                    }
                    else {
                        return <span key={index}><b>{info.key}:</b> {info.value} </span>
                    }
                }
                catch (e) {
                    return object[SystemLogsGetListSchema.information];
                }
            })
        }
        </>
    )
    return (
        <span style={{ fontSize: '8pt', color: cellColor }}>
            { information }
        </span>
    )
});

const applicationCell = React.memo(({value}) => {
    switch (value) {
        case 'SMP':
            return <img src={computerIcon} title={value} alt="" />;
        default:
            return <img src={globeIcon} title={value} alt="" />;
    }
});


export function useSystemLogsResults() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { enqueueErrorSnackbar } = useSMCSnackbar();
    const { sortBasis, sortDirection, setSort } = useSort('date', SortDirections.DESCENDING);

    const columns = useMemo(() => ([
        { id: 'severity',      accessor: SystemLogsGetListSchema.logSeverity,   header: '',                              width: 32,  align: 'center', name: t('SMC_LOGS_TABLE_SEVERITY'), defaultShow: true, sort: buildStringSortCaseInsensitive(SystemLogsGetListSchema.logSeverity),   Cell: severityCell    },
        { id: 'date',          accessor: SystemLogsGetListSchema.date,          header: t('SMC_LOGS_TABLE_DATE'),        width: 115,                                                      defaultShow: true, sort: buildNumberSort(SystemLogsGetListSchema.date),                         Cell: dateCell        },
        { id: 'origin',        accessor: SystemLogsGetListSchema.hostName,      header: t('SMC_LOGS_TABLE_ORIGIN'),      width: 100,                                                      defaultShow: true, sort: buildStringSortCaseInsensitive(SystemLogsGetListSchema.hostName),      Cell: coloredCell     },
        { id: 'serviceDomain', accessor: SystemLogsGetListSchema.serviceDomain, header: t('SMC_LOGS_TABLE_PORTAL'),      width: 125,                                                      defaultShow: true, sort: buildStringSortCaseInsensitive(SystemLogsGetListSchema.serviceDomain), Cell: coloredCell     },
        { id: 'application',   accessor: SystemLogsGetListSchema.application,   header: t('SMC_LOGS_TABLE_APPLICATION'), width: 60,  align: 'center',                                     defaultShow: true, sort: buildStringSortCaseInsensitive(SystemLogsGetListSchema.application),   Cell: applicationCell },
        { id: 'user',          accessor: SystemLogsGetListSchema.user,          header: t('SMC_LOGS_TABLE_USER'),        width: 100,                                                      defaultShow: true, sort: buildStringSortCaseInsensitive(SystemLogsGetListSchema.user),          Cell: userCell        },
        { id: 'message',       accessor: SystemLogsGetListSchema.message,       header: t('SMC_LOGS_TABLE_LOGMSG'),      width: 150,                                                      defaultShow: true, sort: buildStringSortCaseInsensitive(SystemLogsGetListSchema.message),       Cell: coloredCell     },
        { id: 'information',                                                    header: t('SMC_LOGS_TABLE_INFO'),        width: 130,                                                      defaultShow: true, sort: buildStringSortCaseInsensitive(SystemLogsGetListSchema.information),   Cell: infoCell        }
    ]), [t]);

    const rowIdSelector = (obj, index) => index;
    
    const defaultColumnsState = useMemo(() => (
        columns.map(column => ({
            id: column.id,
            show: Boolean(column.defaultShow)
        }))
    ), [columns]);
    const [columnsState, setColumnsState] = useColumnsStatePersistedState(ObjectTypes.SYSTEM_LOGS, defaultColumnsState);
    const setDefaultColumnsState = useCallback(() => setColumnsState(defaultColumnsState), [defaultColumnsState, setColumnsState]);

    const [columnsWidths, setColumnsWidths] = useColumnsWidthsPersistedState(ObjectTypes.SYSTEM_LOGS, null);

    const [isPageLoaded, setIsPageLoaded] = useState(false);

    const {
        tableContent,
        isFetching,
        fetchNextPage,
        isLastPage,
        reloadTable,
        filterObject,
        setFilterObject,
        freeTextSearch
    } = useGenericResultsDataApi(ObjectTypes.SYSTEM_LOGS);

    const reloadButtonOnClick = useCallback(() => {
        reloadTable()
            .catch(errorCode => {
                enqueueErrorSnackbar(t(errorCode));
            });
    }, [reloadTable, enqueueErrorSnackbar, t]);

    const toExcelAction = useCallback(() => {
        enqueueSnackbar(null, {
            persist: true,
            content: (key) => (
                <LogsToExcelSnackbarContent
                    objectType={ObjectTypes.SYSTEM_LOGS}
                    filterObject={filterObject}
                    id={key}
                />
            )
        })
    }, [filterObject, enqueueSnackbar]);

    // Reload table on first time loading
    useEffect(() => {
        reloadTable()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTable,  enqueueErrorSnackbar, t]);

    return ({
        tableContent,
        isFetching,
        isPageLoaded,
        fetchNextPage,
        isLastPage,
        reloadTable,
        columns,
        sortBasis,
        sortDirection,
        setSort,
        columnsState,
        setColumnsState,
        setDefaultColumnsState,
        columnsWidths,
        setColumnsWidths,
        rowIdSelector,
        filterObject,
        setFilterObject,
        freeTextSearch,
        reloadButtonOnClick,
        toExcelAction
    });
}